import React, { useState } from "react";
import { X } from "lucide-react";

/**
 * CompaniesSplitBar — part-to-whole split of visible employees per working
 * company as ONE horizontal stacked bar (per the dataviz form table:
 * part-to-whole → stacked bar, horizontal for long names).
 *
 * Categorical palette (validated, fixed slot order, never cycled); companies
 * beyond slot 5 fold into "Other". 2px surface gaps separate segments; a
 * legend always accompanies ≥2 series and inline segment labels appear only
 * where they fit.
 */
const SLOTS = {
  light: ["#2E7CBA", "#1baf7a", "#eda100", "#4a3aa7", "#e34948"],
  dark: ["#4a9be0", "#199e70", "#c98500", "#9085e9", "#e66767"],
};

const CompaniesSplitBar = ({ data = [], theme = "light", language = "en" }) => {
  const dark = theme === "dark";
  const isRTL = language === "ar";
  const [hover, setHover] = useState(null);
  const [otherOpen, setOtherOpen] = useState(false);

  const palette = dark ? SLOTS.dark : SLOTS.light;
  const surface = dark ? "#1e293b" : "#ffffff";
  const textPrimary = dark ? "#f1f5f9" : "#1e293b";
  const textSecondary = dark ? "rgba(255,255,255,0.55)" : "#64748b";

  // fold the tail into "Other" instead of generating extra hues
  const MAX = palette.length;
  const head = data.slice(0, MAX - (data.length > MAX ? 1 : 0));
  const tail = data.slice(head.length);
  const rows = [
    ...head,
    ...(tail.length
      ? [{
          name: isRTL ? "أخرى" : "Other",
          count: tail.reduce((s, d) => s + d.count, 0),
          isOther: true,
          items: tail,
        }]
      : []),
  ];
  const total = rows.reduce((s, d) => s + d.count, 0) || 1;
  const otherRow = rows.find((d) => d.isOther);

  return (
    <div style={{ minWidth: 0 }}>
      {/* the stacked bar — 2px surface gaps between segments */}
      <div
        style={{
          display: "flex",
          height: 22,
          borderRadius: 6,
          overflow: "hidden",
          gap: 2,
          background: surface,
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {rows.map((d, i) => {
          const pct = (d.count / total) * 100;
          const dim = hover !== null && hover !== i;
          // inline % label only when the segment is wide enough to hold it
          const showInline = pct >= 9;
          return (
            <div
              key={d.name}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={d.isOther ? () => setOtherOpen(true) : undefined}
              title={
                d.isOther
                  ? (isRTL ? "اضغط لعرض التفاصيل" : "Click to see details")
                  : `${d.name}: ${d.count} (${Math.round(pct)}%)`
              }
              style={{
                width: `${pct}%`,
                minWidth: 3,
                background: palette[i % palette.length],
                opacity: dim ? 0.35 : 1,
                transition: "opacity .15s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: d.isOther ? "pointer" : "default",
              }}
            >
              {showInline && (
                <span style={{ fontSize: 10.5, fontWeight: 600, color: "#ffffff" }}>
                  {Math.round(pct)}%
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* legend — identity never rides on color alone */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 12 }}>
        {rows.map((d, i) => (
          <span
            key={d.name}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            onClick={d.isOther ? () => setOtherOpen(true) : undefined}
            title={d.isOther ? (isRTL ? "اضغط لعرض التفاصيل" : "Click to see details") : undefined}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: textSecondary,
              opacity: hover !== null && hover !== i ? 0.45 : 1,
              transition: "opacity .15s",
              cursor: d.isOther ? "pointer" : "default",
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: palette[i % palette.length],
                flexShrink: 0,
              }}
            />
            <span
              style={{
                color: textPrimary,
                fontWeight: 500,
                textDecoration: d.isOther ? "underline" : "none",
                textDecorationStyle: "dotted",
              }}
            >
              {d.name}
            </span>
            {d.count}
          </span>
        ))}
      </div>

      {otherOpen && otherRow && (
        <div
          onClick={() => setOtherOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: dark ? "#1e293b" : "#fff",
              borderRadius: 14,
              padding: 20,
              width: "min(420px, 90vw)",
              maxHeight: "70vh",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: textPrimary }}>
                {otherRow.name}
              </h3>
              <button
                onClick={() => setOtherOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: textSecondary,
                  padding: 4,
                  display: "flex",
                }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ margin: "0 0 12px", fontSize: 12.5, color: textSecondary }}>
              {isRTL
                ? `دي الشركات الأصغر (${otherRow.items.length}) اللي بعد أكبر ${MAX - 1} شركات، بنجمعها في قطاع واحد عشان الرسم يفضل واضح.`
                : `The ${otherRow.items.length} smallest companies (beyond the top ${MAX - 1}) are grouped into this segment to keep the chart readable.`}
            </p>

            <div>
              {otherRow.items
                .slice()
                .sort((a, b) => b.count - a.count)
                .map((item) => {
                  const p = (item.count / total) * 100;
                  return (
                    <div
                      key={item.name}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "7px 0",
                        borderBottom: dark
                          ? "1px solid rgba(255,255,255,0.06)"
                          : "1px solid rgba(15,23,42,0.06)",
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: textPrimary }}>{item.name}</span>
                      <span style={{ color: textSecondary, whiteSpace: "nowrap" }}>
                        {item.count}{" "}
                        <span style={{ opacity: 0.7 }}>({p.toFixed(1)}%)</span>
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesSplitBar;
